import { jsx } from 'react/jsx-runtime';
import { randomString, ABC, wrap } from '@lemonade-hq/ts-helpers';
import PubNub from 'pubnub';
import { createContext, useMemo, useEffect, useCallback, useContext } from 'react';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
// istanbul ignore next
var RealtimeContext = /*#__PURE__*/ createContext({
    addChannels: function() {},
    removeChannels: function() {},
    subscribe: function() {},
    unsubscribe: function() {}
});
var RealtimeProvider = function(param) {
    var channels = param.channels, children = param.children, pubnubSubscribeKey = param.pubnubSubscribeKey, userId = param.userId;
    var subscriptions = useMemo(function() {
        return new Map();
    }, []);
    // initialize PubNub
    var pubnub = useMemo(function() {
        if (pubnubSubscribeKey == null) return undefined;
        var userFinalId = userId !== null && userId !== void 0 ? userId : randomString(32, ABC);
        var id = userId != null ? {
            userId: userFinalId
        } : {
            uuid: userFinalId
        };
        return new PubNub(_object_spread({
            subscribeKey: pubnubSubscribeKey
        }, id));
    }, [
        pubnubSubscribeKey,
        userId
    ]);
    useEffect(function() {
        pubnub === null || pubnub === void 0 ? void 0 : pubnub.addListener({
            message: function(event) {
                var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
                try {
                    for(var _iterator = subscriptions.entries()[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                        var entry = _step.value;
                        var _entry = _sliced_to_array(entry, 2), key = _entry[0], handlers = _entry[1];
                        if (key === event.message.type) {
                            handlers.forEach(function(handler) {
                                return void handler({
                                    type: event.message.type,
                                    payload: event.message.payload
                                });
                            });
                        }
                    }
                } catch (err) {
                    _didIteratorError = true;
                    _iteratorError = err;
                } finally{
                    try {
                        if (!_iteratorNormalCompletion && _iterator.return != null) {
                            _iterator.return();
                        }
                    } finally{
                        if (_didIteratorError) {
                            throw _iteratorError;
                        }
                    }
                }
            }
        });
        pubnub === null || pubnub === void 0 ? void 0 : pubnub.subscribe({
            channels: channels
        });
        return function() {
            pubnub === null || pubnub === void 0 ? void 0 : pubnub.unsubscribe({
                channels: channels
            });
        };
    }, [
        channels,
        pubnub,
        subscriptions
    ]);
    // subscribe function
    var subscribe = useCallback(function(param) {
        var eventType = param.eventType, onEvent = param.onEvent;
        var eventSubscriptions = subscriptions.get(eventType);
        if (eventSubscriptions == null) {
            subscriptions.set(eventType, new Set([
                onEvent
            ]));
        } else {
            eventSubscriptions.add(onEvent);
        }
    }, [
        subscriptions
    ]);
    // unsubscribe function (for dismount)
    var unsubscribe = useCallback(function(param) {
        var eventType = param.eventType, onEvent = param.onEvent;
        var eventSubscriptions = subscriptions.get(eventType);
        eventSubscriptions === null || eventSubscriptions === void 0 ? void 0 : eventSubscriptions.delete(onEvent);
    }, [
        subscriptions
    ]);
    // add channels to listen to dynamically
    var addChannels = useCallback(function(channelsToAdd) {
        pubnub === null || pubnub === void 0 ? void 0 : pubnub.subscribe({
            channels: wrap(channelsToAdd)
        });
    }, [
        pubnub
    ]);
    // remove channels to listen to dynamically
    var removeChannels = useCallback(function(channelsToRemove) {
        pubnub === null || pubnub === void 0 ? void 0 : pubnub.unsubscribe({
            channels: wrap(channelsToRemove)
        });
    }, [
        pubnub
    ]);
    var contextValue = useMemo(function() {
        return {
            subscribe: subscribe,
            unsubscribe: unsubscribe,
            addChannels: addChannels,
            removeChannels: removeChannels
        };
    }, [
        subscribe,
        unsubscribe,
        addChannels,
        removeChannels
    ]);
    return /*#__PURE__*/ jsx(RealtimeContext.Provider, {
        value: contextValue,
        children: children
    });
};
function useRealtimeContext() {
    return useContext(RealtimeContext);
}
function useRealtimeSubscription(param) {
    var eventType = param.eventType, onEvent = param.onEvent;
    var realtimeContext = useContext(RealtimeContext);
    useEffect(function() {
        realtimeContext.subscribe({
            eventType: eventType,
            onEvent: onEvent
        });
        return function() {
            realtimeContext.unsubscribe({
                eventType: eventType,
                onEvent: onEvent
            });
        };
    }, [
        realtimeContext,
        eventType,
        onEvent
    ]);
}

export { RealtimeContext, RealtimeProvider, useRealtimeContext, useRealtimeSubscription };
